import React, { useContext, useState } from 'react';
import DataTable, { createTheme } from 'react-data-table-component';
import { useHistory } from 'react-router-dom';
import { ThemeContext } from 'styled-components';
import { icons } from '../../assets';
import { patchErrorAlertVisualized } from '../../services';
import {
  getNotificationAlarmType,
  getNotificationClient,
  getNotificationDate,
  getNotificationMachine,
} from '../../utils/notification-alerts';
import { notifyError } from '../../utils';
import { Loader } from '..';
import * as s from './styled-table';

// Retorna a cor da severidade do alerta para o dot lateral. Alarme =
// vermelho, atencao/warning = laranja.
const getSeverityColor = (row, colors) => {
  if (row && row.isAlarm) return colors.red;
  return colors.orange;
};

const Table = ({ loading, data, onVisualizeNotification }) => {
  const { title, colors } = useContext(ThemeContext);
  const history = useHistory();
  const [pendingVisualized, setPendingVisualized] = useState([]);

  createTheme('darkmode', {
    background: {
      default: colors.card,
    },
    divider: {
      default: colors.disabled,
    },
    highlightOnHover: {
      default: colors.lightgrey,
    },
  });

  const handleOpenThing = ({ thing, appliance, isAlarm }) => {
    if (!thing?.thing) {
      notifyError('Não foi possível identificar a máquina do alerta');
      return;
    }

    history.push('/dashboard/customer/plant', {
      ...appliance,
      status: appliance?.status || {
        W: 0,
        E: isAlarm ? 1 : 0,
        O: 0,
        OFF: 0,
        NotConnected: 0,
      },
      thing: [
        {
          ...thing,
          model: thing?.model || appliance?.model || '',
        },
      ],
      isAlarm,
    });
  };

  const handleVisualize = async (event, { eventId, visualized }) => {
    event.stopPropagation();

    if (visualized || !eventId) return;

    setPendingVisualized((current) => [...current, eventId]);

    try {
      await patchErrorAlertVisualized({ eventId });

      if (onVisualizeNotification) onVisualizeNotification(eventId);
    } catch (error) {
      notifyError('Não foi possível marcar o alerta como visualizado');
    } finally {
      setPendingVisualized((current) =>
        current.filter((pendingEventId) => pendingEventId !== eventId)
      );
    }
  };

  const columns = [
    {
      // name como ReactNode para alinhar o cabecalho a esquerda (o
      // customStyles.headCells global centraliza). justifyContent
      // flex-start no style alinha o conteudo da CELL.
      name: (
        <div style={{ width: '100%', textAlign: 'left', paddingLeft: 4 }}>
          Cliente
        </div>
      ),
      width: '280px',
      cell: (row) => (
        // Dot de severidade no inicio da linha — vermelho para alarme,
        // laranja para atencao. Operador triage de relance.
        <s.Label color={colors.darkerblue}>
          <s.SeverityDot color={getSeverityColor(row, colors)} />
          {getNotificationClient(row)}
        </s.Label>
      ),
      style: {
        justifyContent: 'flex-start',
        textAlign: 'left',
        paddingLeft: '20px',
      },
    },
    {
      name: 'Máquina',
      width: '240px',
      cell: (row) => (
        <s.Label color={colors.darkerblue}>
          {getNotificationMachine(row)}
        </s.Label>
      ),
    },
    {
      name: 'Data',
      width: '180px',
      cell: (row) => <s.Label>{getNotificationDate(row)}</s.Label>,
      style: { color: colors.label, fontWeight: 600 },
    },
    {
      name: 'Status',
      width: '170px',
      cell: (row) => (
        // Pill colorida em vez de checkbox + texto: vermelho "Pendente"
        // / verde "Visualizado". A acao de marcar foi para a coluna
        // de acoes (botao com check), separando display do controle.
        <s.StatusPill visualized={row.visualized}>
          <s.StatusDot />
          {row.visualized ? 'Visualizado' : 'Pendente'}
        </s.StatusPill>
      ),
    },
    {
      name: 'Tipo de alarme',
      width: '200px',
      cell: (row) => (
        <s.Label
          color={
            row.isAlarm
              ? title === 'dark'
                ? colors.white
                : colors.black
              : colors.orange
          }
        >
          {getNotificationAlarmType(row)}
        </s.Label>
      ),
    },
    {
      name: 'Ações',
      width: '140px',
      cell: (row) => {
        const isPending = pendingVisualized.includes(row.eventId);
        return (
          <s.RowActions>
            <s.IconButton
              type="button"
              disabled={row.visualized || isPending}
              onClick={(event) => handleVisualize(event, row)}
              aria-label={
                row.visualized
                  ? 'Já visualizado'
                  : 'Marcar como visualizado'
              }
              title={
                row.visualized
                  ? 'Já visualizado'
                  : 'Marcar como visualizado'
              }
            >
              {/* Check via SVG inline para evitar adicionar asset novo */}
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke={row.visualized ? colors.green : colors.darkerblue}
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </s.IconButton>
            <s.IconButton
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                handleOpenThing(row);
              }}
              aria-label="Ver máquina"
              title="Ver máquina"
            >
              <s.Icon src={icons.EyeSee} size="20px" margin="0" />
            </s.IconButton>
          </s.RowActions>
        );
      },
      style: { paddingLeft: 5 },
    },
  ];

  return (
    <DataTable
      theme={title === 'light' ? 'default' : 'darkmode'}
      noHeader
      columns={columns}
      data={data}
      highlightOnHover
      pointerOnHover
      fixedHeader
      responsive
      pagination={false}
      persistTableHead
      progressPending={loading}
      noDataComponent="Não há alertas para serem exibidos"
      onRowClicked={handleOpenThing}
      customStyles={{
        table: {
          style: {
            flex: 1,
            width: '100%',
            marginTop: '10px',
            marginBottom: '5px',
            height: '100%',
            paddingBottom: '1.0vh',
          },
        },
        headRow: {
          style: {
            borderBottomColor: colors.disabled,
            minHeight: '68px',
          },
        },
        rows: {
          style: {
            borderBottomColor: colors.disabled,
            minHeight: '68px',
          },
        },
        cells: {
          style: {
            display: 'flex',
            justifyContent: 'center',
            textAlign: 'center',
            paddingLeft: '10px',
            paddingRight: '10px',
            fontSize: '15px',
          },
        },
        headCells: {
          style: {
            display: 'flex',
            justifyContent: 'center',
            textAlign: 'center',
            paddingLeft: '10px',
            paddingRight: '10px',
            fontSize: '15px',
          },
        },
      }}
      progressComponent={
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flex: 1,
            fontSize: 20,
            height: 200,
          }}
        >
          <Loader />
        </div>
      }
    />
  );
};

export default Table;
